import { isFulfilled } from "@reduxjs/toolkit";
import { useFormik } from "formik";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { NOTIFICATION_TIME_OUT } from "../../../constants/app";
import { useAppDispatch, useAppSelector } from "../../../store";
import { showNotificationAction } from "../../../store/application/slice";
import { submitCandidateTimeSheetThunk } from "../../../store/timesheet/thunks";
import {
  ISubmitTimesheetParams,
  TTimesheetVacancyData,
} from "../../../store/timesheet/types";
import { createValidationSchema } from "../../../utils/createValidationSchema";
import CustomButton from "../../UI/Buttons/CustomButton";
import Break from "../../UI/Icons/Break";
import LogIn from "../../UI/Icons/LogIn";
import LogOut from "../../UI/Icons/LogOut";
import RoundedTick from "../../UI/Icons/RoundedTick";
import TimePicker12Hr from "../../UI/Inputs/TimePicker";
import PopupModal from "../../UI/Popup";
import ToggleButton, { ToggleOption } from "../../UI/ToggleButton";
import ErrorMessage from "../../UI/Tooltips/ErrorMessage";
import CentreImage from "../../Vacancy/CentreImage";

interface EditTimeSheetProps {
  /** Data for the timesheet vacancy to be displayed */
  timeSheetData?: TTimesheetVacancyData | null;
  /** Function to control the visibility of the edit modal */
  setShowEditModal: (value: boolean) => void;
  /** Modal visibility state */
  showEditModal: boolean;
  /** Function to load the active timesheet after submission */
  loadActiveTimesheet?: () => Promise<void>;
  /** Function to extract time from date string */
  getTimeFromDate: (date?: string | null, is24HourFormat?: boolean) => string;
  /** Function to extract date from date string */
  getDateFromDateString?: (dateString: string) => string;
  /** Function to combine date and time into UTC formatted string */
  getUtcFormatDateTime?: (
    datestr: string | null,
    timestr: string | null
  ) => string;
}

/**
 * EditTimeSheet component allows a candidate to edit timesheet details.
 *
 * This modal displays the details of a timesheet, including job timeframe, centre information,
 * and allows the candidate to edit and submit the timesheet details (job start/end time, break times).
 * The form is validated and disables submission if unchanged or invalid.
 *
 * @component
 * @param {EditTimeSheetProps} props - The props for the component.
 * @param {any} props.timeSheetData - The timesheet data object to edit.
 * @param {(start: string | null, end: string | null) => string} props.formatJobTime - Formats the job timeframe for display.
 * @param {(show: boolean) => void} props.setShowEditModal - Function to control the visibility of the edit modal.
 * @param {boolean} props.showEditModal - Whether the edit modal is visible.
 * @param {() => void} props.loadActiveTimesheet - Callback to reload the active timesheet after submission.
 * @param {(date: string, is24Hour?: boolean) => string} props.getTimeFromDate - Extracts time from a date string.
 * @param {(dateString: string) => string} props.getDateFromDateString - Extracts date from a date string.
 * @param {(date: string | null, time: string | null) => string} props.getUtcFormatDateTime - Combines date and time into a UTC datetime string.
 *
 * @returns {JSX.Element} The rendered EditTimeSheet modal component.
 */
const EditTimeSheet: React.FC<EditTimeSheetProps> = ({
  timeSheetData,
  setShowEditModal,
  showEditModal,
  loadActiveTimesheet,
  getTimeFromDate,
  getDateFromDateString,
  getUtcFormatDateTime,
}) => {
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.auth.user);
  const [isDisabled, setIsDisabled] = useState<boolean>(true);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [iDidNotWorkOnThisDay, setIDidNotWorkOnThisDay] = useState<boolean>(false);
  const [selectedWorkType, setSelectedWorkType] = useState('worked');
  const [savedTimeValues, setSavedTimeValues] = useState({
    job_start_time: "",
    job_end_time: "",
    break_start_time: "",
    break_end_time: "",
  });

  const options: ToggleOption[] = [
    { label: 'I did work', value: 'worked' },
    { label: 'I did not work on this day', value: 'not-worked' }
  ];



  // Helper to format date from date string to YYYY-MM-DD format
  const getDateFromDate = useCallback((dateString: string | null): string => {
    if (!dateString) return "";
    
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return "";
    
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }, []);

  // Helper to extract initial time values from timeSheetData
  const getInitialTimes = useCallback((): ISubmitTimesheetParams => ({
    job_start_time: getTimeFromDate(timeSheetData?.job_start_time ?? "", true),
    job_end_time: getTimeFromDate(timeSheetData?.job_end_time ?? "", true),
    break_start_time: getTimeFromDate(timeSheetData?.break_start_time ?? "", true),
    break_end_time: getTimeFromDate(timeSheetData?.break_end_time ?? "", true),
    candidate_id: user?.uuid || "",
    vacancy_id: timeSheetData?.vacancy_id || "",
    timeframe_id: timeSheetData?.vacancy_timeframe_id || 0,
    did_not_work: timeSheetData?.did_not_work,
  }), [timeSheetData, getTimeFromDate, user?.uuid]);

  // Calculate job timeframe for validation
  const jobTimeframe = useMemo(() => {
    if (!getDateFromDateString || !getUtcFormatDateTime || !timeSheetData) {
      return { startTime: "", endTime: "" };
    }

    const initialDidNotWork =  getInitialTimes().did_not_work || false;
    const jobDate = !initialDidNotWork ? getDateFromDateString(timeSheetData.job_start_time ?? "") : getDateFromDateString(timeSheetData.timesheet_date ?? "");
    const jobStartDateTime = getUtcFormatDateTime(
      jobDate,
      timeSheetData.timeframe?.start_time ?? null
    );
    const jobEndDateTime = getUtcFormatDateTime(
      jobDate,
      timeSheetData.timeframe?.end_time ?? null
    );

    return {
      startTime: jobStartDateTime ? getTimeFromDate(jobStartDateTime, true) : "",
      endTime: jobEndDateTime ? getTimeFromDate(jobEndDateTime, true) : "",
    };
  }, [timeSheetData, getDateFromDateString, getUtcFormatDateTime, getTimeFromDate]);



  const { handleSubmit, values, errors, setFieldValue, resetForm, validateForm } =
    useFormik<ISubmitTimesheetParams>({
      validationSchema: iDidNotWorkOnThisDay ? undefined : createValidationSchema(
        "candidate_log_timesheet",
        jobTimeframe.startTime,
        jobTimeframe.endTime
      ),
      initialValues: getInitialTimes(),
      enableReinitialize: true,
      validateOnChange: true,
      validateOnBlur: true,
      onSubmit: async (values: ISubmitTimesheetParams) => {
        if (!timeSheetData) return;
        setIsSubmitting(true);
        const baseDate = iDidNotWorkOnThisDay ? getDateFromDate(timeSheetData.job_start_time) : getDateFromDate(timeSheetData.timesheet_date);
        const payload: ISubmitTimesheetParams = {
          job_start_time: `${baseDate} ${values.job_start_time}`,
          job_end_time: `${baseDate} ${values.job_end_time}`,
          break_start_time: values.break_start_time 
            ? `${baseDate} ${values.break_start_time}`
            : undefined,
          break_end_time: values.break_end_time 
            ? `${baseDate} ${values.break_end_time}`
            : undefined,
          vacancy_id: timeSheetData.vacancy_id || "",
          candidate_id: user?.uuid || "",
          timeframe_id: timeSheetData.vacancy_timeframe_id || 0,
          did_not_work: iDidNotWorkOnThisDay,
          timesheet_date: timeSheetData.timesheet_date || "",
          
        };

        const response = await dispatch(submitCandidateTimeSheetThunk(payload));
        if (isFulfilled(response)) {
          setIsSubmitting(false);
          setShowEditModal(false);
          resetForm();
          setSelectedWorkType('worked');
          setIDidNotWorkOnThisDay(false);
          dispatch(showNotificationAction("Submitted"));
          setTimeout(() => {
            loadActiveTimesheet?.();
          }, NOTIFICATION_TIME_OUT);
        }
        setIsSubmitting(false);
      },
    });

  // Function to handle the close action of the modal
  const onClickClose = useCallback(() => {
    resetForm(); // Reset form to initial values when closing
    setIDidNotWorkOnThisDay(false); // Reset checkbox state
    setSelectedWorkType('worked'); // Reset toggle state
    setSavedTimeValues({ // Reset saved time values
      job_start_time: "",
      job_end_time: "",
      break_start_time: "",
      break_end_time: "",
    });
    setShowEditModal(false);
  }, [setShowEditModal, resetForm]);

  // Reset form when modal is opened (in case it was closed without saving)
  useEffect(() => {
    if (showEditModal && timeSheetData) {
      resetForm();
      const didNotWork = timeSheetData.did_not_work || false;
      setIDidNotWorkOnThisDay(didNotWork);
      setSelectedWorkType(didNotWork ? 'not-worked' : 'worked');
      // Clear saved time values when opening modal
      setSavedTimeValues({
        job_start_time: "",
        job_end_time: "",
        break_start_time: "",
        break_end_time: "",
      });
    }
  }, [showEditModal, timeSheetData, resetForm]);

  // Store initial values for comparison to enable/disable submit button
  const initialValuesRef = useRef(getInitialTimes());

  // Update initial values reference when timesheet data changes
  useEffect(() => {
    initialValuesRef.current = getInitialTimes();
  }, [getInitialTimes]);

  // Update submit button state based on form changes and validation
  useEffect(() => {
    const initial = initialValuesRef.current;
    const initialDidNotWork = initial.did_not_work || false;
    
    // If "I did not work on this day" is checked
    if (iDidNotWorkOnThisDay) {
      // Enable if the checkbox state has changed from initial state
      // or if it was already checked initially (allow re-submission)
      setIsDisabled(iDidNotWorkOnThisDay === initialDidNotWork && 
                    values.job_start_time === initial.job_start_time &&
                    values.job_end_time === initial.job_end_time &&
                    values.break_start_time === initial.break_start_time &&
                    values.break_end_time === initial.break_end_time);
      return;
    }
    
    const isUnchanged = (
      values.job_start_time === initial.job_start_time &&
      values.job_end_time === initial.job_end_time &&
      values.break_start_time === initial.break_start_time &&
      values.break_end_time === initial.break_end_time &&
      iDidNotWorkOnThisDay === initialDidNotWork
    );
    const hasErrors = Object.keys(errors).length > 0;
    
    // Check if required fields are filled (job_start_time and job_end_time are required)
    const requiredFieldsFilled = values.job_start_time && values.job_end_time;
    
    setIsDisabled(isUnchanged || hasErrors || !requiredFieldsFilled);
  }, [values, errors, iDidNotWorkOnThisDay]);

  const handleTimeChange = useCallback((value: string | null, fieldName: string) => {
    if (value) {
      setFieldValue(fieldName, value);
      // Trigger validation after a short delay to ensure the value is set
      setTimeout(() => {
        validateForm();
      }, 0);
    }
  }, [setFieldValue, validateForm]);

  const handleWorkTypeToggle = (value: string | number) => {
    const newValue = String(value);
    setSelectedWorkType(newValue);
    
    if (newValue === 'not-worked') {
      // Save current values before clearing
      setSavedTimeValues({
        job_start_time: values.job_start_time,
        job_end_time: values.job_end_time,
        break_start_time: values.break_start_time || "",
        break_end_time: values.break_end_time || "",
      });
      
      // Clear all time fields
      setFieldValue("job_start_time", "", false);
      setFieldValue("job_end_time", "", false);
      setFieldValue("break_start_time", "", false);
      setFieldValue("break_end_time", "", false);
      
      setIDidNotWorkOnThisDay(true);
    } else {
      // Restore saved values when switching back to 'worked'
      setFieldValue("job_start_time", savedTimeValues.job_start_time, false);
      setFieldValue("job_end_time", savedTimeValues.job_end_time, false);
      setFieldValue("break_start_time", savedTimeValues.break_start_time, false);
      setFieldValue("break_end_time", savedTimeValues.break_end_time, false);
      
      setIDidNotWorkOnThisDay(false);
      
      // Trigger validation after values are restored
      setTimeout(() => {
        validateForm();
      }, 0);
    }
  };


  return (
    <PopupModal
      visible={showEditModal}
      showCloseBtn={true}
      onClickClose={onClickClose}
      customWidth={650}
      popUpClass="mobile-model"
    >
      <div className="d-flex flex-column gap-15">
        <div className="fs-22 fw-400 text-black">Edit Timesheet</div>
        <div className="bg-off-white h-1px"></div>
        
        <div className="d-flex flex-column gap-15 mobile-job-scroll">

            <div className="fs-16 fw-400 text-black">
                You have completed the job below. Please submit your timesheet to
                continue using the portal.
            </div>
          {/* Centre Information */}
          <div className="d-flex gap-20">
            <div className="w-50px h-50px border-2 border-grey-dark radius-40px">
              <CentreImage
                imageUrl={timeSheetData?.centre?.photo ?? undefined}
                width="50px"
                height="50px"
                isCandidateFilledAllForms={true}
              />
            </div>
            <div className="flex-grow-1 d-flex flex-column gap-20">
              <div className="flex-grow-1 d-flex flex-column gap-3 text-break">
                <div className="fs-16 fw-700 text-black">
                  {timeSheetData?.centre?.business_name},{" "}
                  <span className="fw-400">{timeSheetData?.centre?.address}</span>
                </div>
                <div className="fs-13 fw-400 text-black">
                  {timeSheetData?.vacancy?.matching_position?.name || ""}
                </div>
              </div>
              <div className="d-flex flex-column gap-3 fs-13 text-black">
                <div className="fw-700">Job Timeframe</div>
                <div className="fw-400">
                  {/* {timeSheetData && formatReadableTimeSlot(
                    format(new Date(timeSheetData.job_start_time ?? ""), "yyyy-MM-dd"), 
                    timeSheetData.timeframe?.start_time || "", 
                    timeSheetData.timeframe?.end_time || ""
                  )} */}
                  {
                    timeSheetData?.timesheet_date_time || "-"
                  }
                </div>
              </div>
            </div>
          </div>

          <div className="h-1px"></div>
          <div className="fs-16 fw-700 text-black">Log the details for the above job:</div>
          <div className="h-1px"></div>
          
          <ToggleButton
            options={options}
            selectedValue={selectedWorkType}
            onChange={handleWorkTypeToggle}
          />
          {selectedWorkType === 'worked' && (
            <>
              {/* Job Start Time */}
              <div className="d-flex flex-column">
            <div className="d-flex gap-15 gap-mobile-8 align-items-center">
              <LogIn />
              <div className="fs-16 fw-400 text-black min-w-140px mobile-min-w-65px">
                Job start <span className="d-none d-lg-inline">time</span>
              </div>
              <div className="w-120px mobile-w-95px">
                <TimePicker12Hr
                  onChange={(value) => handleTimeChange(value as string, "job_start_time")}
                  value={values.job_start_time}
                  className="form-select appearance-none"
                />
              </div>
            </div>
            {errors.job_start_time && (
              <ErrorMessage message={errors.job_start_time} />
            )}
          </div>

          {/* Break Times */}
          <div className="d-flex flex-column">
            <div className="d-flex gap-15 gap-mobile-8 align-items-center">
              <Break />
              <div className="d-flex flex-column min-w-140px mobile-min-w-65px">
                <div className="fs-16 fw-400 text-black">
                  Break <span className="d-none d-lg-inline">timeframe</span>
                </div>
                <div className="fs-13 fw-400 text-grey-medium">(Optional)</div>
              </div>
              <div className="w-120px mobile-w-95px">
                <TimePicker12Hr
                  onChange={(value) => handleTimeChange(value as string, "break_start_time")}
                  value={values.break_start_time || ""}
                  className="form-select appearance-none"
                />
              </div>
              <div className="fs-16 fw-400 text-black">-</div>
              <div className="w-120px mobile-w-95px">
                <TimePicker12Hr
                  onChange={(value) => handleTimeChange(value as string, "break_end_time")}
                  value={values.break_end_time || ""}
                  className="form-select appearance-none"
                />
              </div>
            </div>
            {(errors.break_start_time || errors.break_end_time) && (
              <>
                {errors.break_start_time && <ErrorMessage message={errors.break_start_time} />}
                {errors.break_end_time && <ErrorMessage message={errors.break_end_time} />}
              </>
            )}
          </div>

          {/* Job End Time */}
          <div className="d-flex flex-column">
            <div className="d-flex gap-15 gap-mobile-8 align-items-center">
              <LogOut color="#167E76" />
              <div className="fs-16 fw-400 text-black min-w-140px mobile-min-w-65px">
                Job end <span className="d-none d-lg-inline">time</span>
              </div>
              <div className="w-120px mobile-w-95px">
                <TimePicker12Hr
                  onChange={(value) => handleTimeChange(value as string, "job_end_time")}
                  value={values.job_end_time || ""}
                  className="form-select appearance-none"
                />
              </div>
            </div>
            {errors.job_end_time && (
              <ErrorMessage message={errors.job_end_time} />
            )}
          </div>
            </>
          )}

          {/* Submit Button */}
          <div className="d-flex justify-content-end">

            <CustomButton
              variant="primary"
              type="button"
              className="btn-theme bg-teal text-black mobile-w-100"
              onClick={() => handleSubmit()}
              disabled={isSubmitting || isDisabled}
              isLoading={isSubmitting}
            >
              <RoundedTick color={isDisabled ? "#aaaaaa" : "black"} />
              Save Changes
            </CustomButton>
          </div>
        </div>
      </div>
    </PopupModal>
  );
};

export default EditTimeSheet;